import {
    PRIMITIVE_LINES,
    Entity,
    Mesh,
    MeshInstance,
    ShaderMaterial,
    Vec3
} from 'playcanvas';

import type { Bookmark } from './bookmarks';
import { Element, ElementType } from './element';
import { vertexShader, fragmentShader } from './shaders/debug-shader';

// temp vectors for frustum geometry calculation (module-scope to avoid allocations)
const tmpForward = new Vec3();
const tmpRight = new Vec3();
const tmpUp = new Vec3();
const tmpBase = new Vec3();
const tmpTL = new Vec3();
const tmpTR = new Vec3();
const tmpBL = new Vec3();
const tmpBR = new Vec3();
const tmpUpTip = new Vec3();
const tmpScreen = new Vec3();
const tmpCamPos = new Vec3();
const tmpToPoint = new Vec3();

// lines per camera icon: 4 pyramid + 4 base rect + 2 up indicator = 10
const LINES_PER_CAMERA = 10;
const VERTS_PER_CAMERA = LINES_PER_CAMERA * 2;

// colors: orange for normal bookmarks, bright yellow for selected
const CLR_NORMAL: [number, number, number, number] = [255, 180, 50, 255];
const CLR_SELECTED: [number, number, number, number] = [255, 255, 80, 255];

type LabelEntry = {
    id: string;
    root: HTMLDivElement;
    position: Vec3;
};

class BookmarkMarkers extends Element {
    entity: Entity;
    mesh: Mesh;
    material: ShaderMaterial;
    meshInstance: MeshInstance;
    dirty = true;

    overlayRoot: HTMLDivElement | null = null;
    labels = new Map<string, LabelEntry>();

    constructor() {
        super(ElementType.debug);
    }

    add() {
        const scene = this.scene;
        const device = scene.graphicsDevice;

        this.material = new ShaderMaterial({
            uniqueName: 'bookmarkMarkersMaterial',
            vertexGLSL: vertexShader,
            fragmentGLSL: fragmentShader
        });
        this.material.depthWrite = true;
        this.material.depthTest = true;
        this.material.update();

        this.mesh = new Mesh(device);
        this.mesh.primitive[0] = {
            baseVertex: 0,
            type: PRIMITIVE_LINES,
            base: 0,
            count: 0
        };

        this.meshInstance = new MeshInstance(this.mesh, this.material, null);
        this.meshInstance.cull = false;

        this.entity = new Entity('bookmarkMarkers');
        this.entity.addComponent('render', {
            meshInstances: [this.meshInstance],
            layers: [scene.worldLayer.id]
        });

        scene.app.root.addChild(this.entity);

        this.createOverlayRoot();

        // mark dirty when bookmarks change
        const markDirty = () => {
            this.dirty = true;
            if (scene.events.invoke('bookmarks.showMarkers')) {
                scene.forceRender = true;
            }
        };
        const { events } = scene;
        events.on('bookmarks.listChanged', markDirty);
        events.on('bookmarks.selectedChanged', markDirty);
        events.on('scene.boundChanged', markDirty);
    }

    destroy() {
        this.clearLabels();
        this.overlayRoot?.remove();
        this.overlayRoot = null;
        this.entity?.destroy();
    }

    onPreRender() {
        const { scene } = this;
        const visible = scene.events.invoke('bookmarks.showMarkers') && scene.camera.renderOverlays;

        this.entity.enabled = visible;
        if (this.overlayRoot) {
            this.overlayRoot.style.display = visible ? 'block' : 'none';
        }

        if (visible && this.dirty) {
            this.dirty = false;
            this.rebuildMesh();
        }
    }

    onPostRender() {
        const visible = this.scene.events.invoke('bookmarks.showMarkers') && this.scene.camera.renderOverlays;
        if (visible) {
            this.updateLabelPositions();
        }
    }

    private createOverlayRoot() {
        const canvasContainer = window.document.getElementById('canvas-container');
        if (!canvasContainer) {
            return;
        }

        const overlayRoot = window.document.createElement('div');
        overlayRoot.id = 'bookmark-labels-overlay';
        canvasContainer.appendChild(overlayRoot);

        this.overlayRoot = overlayRoot;
    }

    private clearLabels() {
        for (const entry of this.labels.values()) {
            entry.root.remove();
        }
        this.labels.clear();
    }

    private createLabel(bookmark: Bookmark) {
        if (!this.overlayRoot) {
            return;
        }

        const root = window.document.createElement('div');
        root.className = 'bookmark-label';
        root.dataset.bookmarkId = bookmark.id;
        root.textContent = bookmark.name;

        root.addEventListener('pointerdown', (e) => {
            e.preventDefault();
            e.stopPropagation();
            this.scene.events.fire('bookmarks.select', bookmark.id);
            this.scene.events.fire('bookmarks.goTo', bookmark.id);
        });

        this.overlayRoot.appendChild(root);

        const position = new Vec3(
            bookmark.cameraPose.position[0],
            bookmark.cameraPose.position[1],
            bookmark.cameraPose.position[2]
        );

        this.labels.set(bookmark.id, { id: bookmark.id, root, position });
    }

    private updateLabelPositions() {
        if (!this.overlayRoot) {
            return;
        }

        const canvasRect = this.scene.canvas.getBoundingClientRect();
        const width = Math.max(1, canvasRect.width);
        const height = Math.max(1, canvasRect.height);

        const cameraPos = tmpCamPos.copy(this.scene.camera.entity.getPosition());
        const cameraForward = this.scene.camera.entity.forward;
        const isOrtho = this.scene.camera.ortho;
        const selectedId = this.scene.events.invoke('bookmarks.selected') as string | null;

        for (const entry of this.labels.values()) {
            const toPoint = tmpToPoint.sub2(entry.position, cameraPos);
            const behindCamera = !isOrtho && toPoint.dot(cameraForward) <= 0;

            this.scene.camera.worldToScreen(entry.position, tmpScreen);
            const sx = tmpScreen.x * width;
            const sy = tmpScreen.y * height;

            const offscreen = tmpScreen.x < -0.02 || tmpScreen.x > 1.02 ||
                tmpScreen.y < -0.02 || tmpScreen.y > 1.02;

            if (behindCamera || offscreen || Number.isNaN(sx) || Number.isNaN(sy)) {
                entry.root.style.display = 'none';
                continue;
            }

            entry.root.style.display = 'inline-flex';
            entry.root.style.transform = `translate(calc(${sx.toFixed(1)}px - 50%), calc(${sy.toFixed(1)}px - 100% - 8px))`;
            entry.root.classList.toggle('selected', entry.id === selectedId);
        }
    }

    private rebuildMesh() {
        const bookmarks = this.scene.events.invoke('bookmarks.list') as Bookmark[];

        // also rebuild labels
        this.clearLabels();

        if (!bookmarks || bookmarks.length === 0) {
            this.mesh.primitive[0].count = 0;
            return;
        }

        const selectedId = this.scene.events.invoke('bookmarks.selected') as string | null;

        const boundSize = this.scene.bound.halfExtents.length();
        const iconScale = boundSize > 0 ? boundSize * 0.02 : 0.1;

        const depth = iconScale * 2;
        const halfW = iconScale * 1.2;
        const halfH = iconScale * 0.9;

        // filter to only visible bookmarks
        const visibleBookmarks = bookmarks.filter(b => b.visible !== false);

        const numVerts = visibleBookmarks.length * VERTS_PER_CAMERA;
        const positions: number[] = [];
        const colors = new Uint8Array(numVerts * 4);

        const pushLine = (a: Vec3, b: Vec3) => {
            positions.push(a.x, a.y, a.z, b.x, b.y, b.z);
        };

        let vertIndex = 0;

        const tmpPos = new Vec3();
        const tmpTgt = new Vec3();

        for (const bookmark of visibleBookmarks) {
            const pose = bookmark.cameraPose;
            if (!pose?.position || !pose?.target) {
                continue;
            }

            tmpPos.set(pose.position[0], pose.position[1], pose.position[2]);
            tmpTgt.set(pose.target[0], pose.target[1], pose.target[2]);

            // forward direction
            tmpForward.sub2(tmpTgt, tmpPos).normalize();

            // right direction (handle degenerate case when looking straight up/down)
            if (Math.abs(tmpForward.y) > 0.999) {
                tmpRight.cross(tmpForward, Vec3.BACK).normalize();
            } else {
                tmpRight.cross(tmpForward, Vec3.UP).normalize();
            }

            // up direction
            tmpUp.cross(tmpRight, tmpForward);

            // base center (in front of camera position)
            tmpBase.copy(tmpPos).addScaled(tmpForward, depth);

            // frustum corners
            tmpTL.copy(tmpBase).addScaled(tmpUp, halfH).addScaled(tmpRight, -halfW);
            tmpTR.copy(tmpBase).addScaled(tmpUp, halfH).addScaled(tmpRight, halfW);
            tmpBL.copy(tmpBase).addScaled(tmpUp, -halfH).addScaled(tmpRight, -halfW);
            tmpBR.copy(tmpBase).addScaled(tmpUp, -halfH).addScaled(tmpRight, halfW);

            // pyramid edges from position to corners
            pushLine(tmpPos, tmpTL);
            pushLine(tmpPos, tmpTR);
            pushLine(tmpPos, tmpBL);
            pushLine(tmpPos, tmpBR);

            // base rectangle
            pushLine(tmpTL, tmpTR);
            pushLine(tmpTR, tmpBR);
            pushLine(tmpBR, tmpBL);
            pushLine(tmpBL, tmpTL);

            // up indicator triangle
            tmpUpTip.copy(tmpBase).addScaled(tmpUp, halfH * 1.5);
            pushLine(tmpTL, tmpUpTip);
            pushLine(tmpTR, tmpUpTip);

            // set color for this bookmark's vertices
            const clr = bookmark.id === selectedId ? CLR_SELECTED : CLR_NORMAL;
            for (let i = 0; i < VERTS_PER_CAMERA; i++) {
                const off = (vertIndex + i) * 4;
                colors[off] = clr[0];
                colors[off + 1] = clr[1];
                colors[off + 2] = clr[2];
                colors[off + 3] = clr[3];
            }
            vertIndex += VERTS_PER_CAMERA;

            // create HTML label overlay
            this.createLabel(bookmark);
        }

        this.mesh.setPositions(positions);
        this.mesh.setColors32(colors);
        this.mesh.update(PRIMITIVE_LINES);
    }
}

export { BookmarkMarkers };
